import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Box, Typography, IconButton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Close } from "@mui/icons-material";
import { getTransportTasks } from "../../../services/transportTasksService";
import { TransportTask } from "../../../models/transport/transportTask";
import { TransportEventType, transportEventTypeNames } from "../../../models/transport/transportEventType";
import { getDateTime } from "./transportUtils";
import * as store from "../../../framework/customStore";
import * as storeActions from "../../../models/store/storeActions";
import { transportTaskTypeNames } from "../../../models/transport/transportTaskType";
import { Base } from "../../../framework/base";

interface IOrderEventLogProps {
    transportOrderId: string;
    show: boolean;
    onClose?: () => void;
}

export const OrderEventLog = ({ transportOrderId, show, onClose }: IOrderEventLogProps) => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState<TransportTask[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const handleClose = () => {
        onClose();
    };

    useEffect(() => {
        if (!transportOrderId) return;

        const fetchTransportTasks = async() => {
            try {
                const { data } = await getTransportTasks(transportOrderId);
                setTasks(data);
            } catch (error) {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(t("transport.order.eventLogFetchError"))
                );
                console.error("Error fetching transport tasks:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTransportTasks().catch((e) => console.error("Error fetching transport tasks:", e));
    }, [transportOrderId]);

    const getGeolocation = (task: TransportTask) => {
        if (task.latitude && task.longitude) {
            return task.latitude + " - " + task.longitude;
        }
        return "-";
    };

    const getDetails = (task: TransportTask) => {
        if (task.eventType === TransportEventType.TransportOrder) {
            let details = task.details;
            // Map of placeholders to translations
            const replacements: [string, string][] = [
                ["[SenderName]", t("transport.order.sender")],
                ["[ReceiverName]", t("transport.order.receiver")],
                ["[BillingCustomer]", t("transport.order.billingCustomer")],
                ["[ScheduledStartDateTime]", t("transport.order.scheduledStartDateBeginning")],
                ["[ScheduledEndDateTime]", t("transport.order.scheduledStartDateEnding")],
            ];
            for (const [placeholder, translation] of replacements) {
                if (details.includes(placeholder)) {
                    details = details.replace(placeholder, translation);
                    // Handle date replacements
                    if (
                        placeholder === "[ScheduledStartDateTime]" ||
                        placeholder === "[ScheduledEndDateTime]"
                    ) {
                        const [label, dateStr] = details.split(": ");
                        return label + ": " + Base.dateStrToOriginalTimezoneDateTimeStr(dateStr);
                    }
                    return details;
                }
            }
            return "-";
        }
        if (task.eventType === TransportEventType.Vehicle) {
            return task.details
                .replace("[VehicleName]", t("common.vehicleName"))
                .replace("[VehicleRegisterNumber]", t("common.vehicleNumber"));
        }
        if (task.eventType === TransportEventType.Driver) {
            return task.details.replace("[DriverName]", t("common.driverName"));
        }
        if (task.eventType === TransportEventType.Product) {
            return task.details
                .replace("[ProductName]", t("transport.order.productName"))
                .replace("[ProductQuantity]", t("transport.order.quantity"));
        }
        return "-";
    };

    return (
        <>
            <Modal
                open={show}
                onClose={handleClose}
                aria-labelledby="notifications-modal-title"
                aria-describedby="notifications-modal-content"
            >
                <Box sx={{
                    position: "absolute" as const,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    bgcolor: "background.paper",
                    borderRadius: "16px",
                    boxShadow: 24,
                    p: 4,
                    maxHeight: "80vh",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                    scrollbarWidth: "none",
                }}
                >
                    <Grid2 sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography id="notifications-modal-title" variant="h6" component="h2">
                            {t("transport.order.eventLog")}
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </Grid2>
                    {loading ? (
                        <Grid2 id="notifications-modal-content" sx={{ mt: 2, minWidth: 750 }}>
                            <Typography variant="body1">{t("common.loading")}</Typography>
                        </Grid2>
                    ) :
                        <Grid2 id="notifications-modal-content" sx={{ mt: 2, minWidth: 750 }}>
                            {tasks.length > 0 ? (
                                <TableContainer>
                                    <Table size="small" aria-label="a dense table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t("transport.order.eventType")}</TableCell>
                                                <TableCell>{t("transport.order.event")}</TableCell>
                                                <TableCell>{t("transport.order.timestamp")}</TableCell>
                                                <TableCell>{t("transport.order.employeeName")}</TableCell>
                                                <TableCell>{t("common.address")}</TableCell>
                                                <TableCell>{t("transport.order.geolocation")}</TableCell>
                                                <TableCell>{t("transport.order.details")}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tasks.map((task) => (
                                                <TableRow
                                                    key={task.id}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {t(transportEventTypeNames[task.eventType])}
                                                    </TableCell>
                                                    <TableCell>{t(transportTaskTypeNames[task.taskType])}</TableCell>
                                                    <TableCell>{getDateTime(task.performedDateTime)}</TableCell>
                                                    <TableCell>{task.employeeName || "-"}</TableCell>
                                                    <TableCell>{task.address || "-"}</TableCell>
                                                    <TableCell>{getGeolocation(task)}</TableCell>
                                                    <TableCell>{getDetails(task)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : <Typography variant="body1">{t("transport.order.noEventLogs")}</Typography>}
                        </Grid2>
                    }
                </Box>
            </Modal>
        </>
    );
};
